/**
 * 모달 관련 스타일 컴포넌트
 */
import styled from 'styled-components';

export const Overlay = styled.div<{ $isOpen: boolean; $position?: { x: number; y: number } }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: ${props => props.$isOpen ? 'block' : 'none'};
  z-index: 2000;
  overflow-y: auto;
  pointer-events: auto;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
    padding-top: ${props => props.theme.spacing.xl};
  }
`;

export const StickyNoteModal = styled.div<{ $color: 'green' | 'yellow' | 'blue'; $position?: { x: number; y: number } }>`
  background-color: ${props => {
    if (props.$color === 'green') return '#EBFFCF';
    if (props.$color === 'yellow') return '#FEFFCF';
    if (props.$color === 'blue') return '#E3F2FF';
    return '#FEFFCF';
  }};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.10);
  min-width: ${props => props.$position ? '280px' : '500px'};
  max-width: ${props => props.$position ? '350px' : '800px'};
  width: ${props => props.$position ? 'auto' : '90%'};
  max-height: ${props => props.$position ? '400px' : 'calc(100vh - 40px)'};
  overflow-x: hidden;
  overflow-y: auto;
  position: ${props => props.$position ? 'fixed' : 'relative'};
  top: ${props => props.$position ? `${props.$position.y}px` : 'auto'};
  left: ${props => props.$position ? `${props.$position.x}px` : 'auto'};
  box-sizing: border-box;
  pointer-events: auto;
  z-index: 2000;
  
  @media (max-width: 768px) {
    min-width: unset;
    width: calc(100% - 32px);
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 64px);
    padding: ${props => props.theme.spacing.md};
    ${props => props.$position ? `
      top: auto;
      left: auto;
      position: relative;
    ` : ''}
  }
`;

export const ColorButton = styled.button<{ $color: 'green' | 'yellow' | 'blue'; $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => {
    if (props.$color === 'green') return '#C1DC9B';
    if (props.$color === 'yellow') return '#E3E4A7';
    if (props.$color === 'blue') return '#C2D2E1';
    return '#E3E4A7';
  }};
  background-color: ${props => {
    if (props.$color === 'green') return '#EBFFCF';
    if (props.$color === 'yellow') return '#FEFFCF';
    if (props.$color === 'blue') return '#E3F2FF';
    return '#FEFFCF';
  }};
  cursor: pointer;
  outline: ${props => props.$isSelected ? '2px solid #333' : 'none'};
  outline-offset: 2px;
  
  &:hover {
    transform: scale(1.1);
  }
`;

// 메모 모달 스타일 컴포넌트
export const NoteModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid currentColor;
  opacity: 0.3;
`;

export const NoteModalTitle = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color2b};
  padding: 0;
  
  &::placeholder {
    color: ${props => props.theme.colors.gray[500]};
  }
  
  &:disabled {
    cursor: default;
  }
`;

export const NoteModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

export const NoteModalIconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.gray[600]};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const NoteModalContent = styled.div`
  padding: ${props => props.theme.spacing.md} 0;
  min-height: 100px;
  overflow-x: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const NoteModalTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.light};
  font-family: inherit;
  color: ${props => props.theme.colors.book.color4b};
  line-height: 1.6;
  padding: 0;
  box-sizing: border-box;
  word-break: break-word;
  overflow-wrap: break-word;
  
  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }
  
  &:disabled {
    cursor: default;
  }
`;

export const NoteModalText = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.light};
  color: ${props => props.theme.colors.book.color4b};
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const NoteModalAttachments = styled.div`
  border-top: 1px solid currentColor;
  opacity: 0.3;
  padding-top: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

export const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

export const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xs} 0;
`;

export const AttachmentName = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[700]};
`;

export const AttachmentDeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[500]};
  
  &:hover {
    color: ${props => props.theme.colors.error[500]};
  }
`;

export const AddAttachmentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px dashed ${props => props.theme.colors.gray[400]};
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  cursor: pointer;
  color: ${props => props.theme.colors.gray[500]};
  margin-top: ${props => props.theme.spacing.sm};
  
  &:hover {
    border-color: ${props => props.theme.colors.gray[600]};
    color: ${props => props.theme.colors.gray[700]};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const NoteMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #2b2b2b;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.xl};
  padding: ${props => props.theme.spacing.xs};
  z-index: 10;
  min-width: 100px;
`;

export const NoteMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  background: transparent;
  color: white;
  font-size: ${props => props.theme.fontSize.sm};
  cursor: pointer;
  border-radius: ${props => props.theme.borderRadius.sm};
  
  &:hover {
    background-color: #3b3b3b;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const NoteColorPickerDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #2b2b2b;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.xl};
  padding: ${props => props.theme.spacing.sm};
  z-index: 10;
  display: flex;
  gap: ${props => props.theme.spacing.xs};
`;

export const NoteColorOption = styled.button<{ $color: 'green' | 'yellow' | 'blue'; $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${props => {
    if (props.$color === 'green') return '#C1DC9B';
    if (props.$color === 'yellow') return '#E3E4A7';
    if (props.$color === 'blue') return '#C2D2E1';
    return '#E3E4A7';
  }};
  background-color: ${props => {
    if (props.$color === 'green') return '#EBFFCF';
    if (props.$color === 'yellow') return '#FEFFCF';
    if (props.$color === 'blue') return '#E3F2FF';
    return '#FEFFCF';
  }};
  cursor: pointer;
  outline: ${props => props.$isSelected ? '2px solid white' : 'none'};
  outline-offset: 2px;
  
  &:hover {
    transform: scale(1.1);
  }
`;

export const NoteModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;
